import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle, Bot, Check, Clock, Coins, ExternalLink, FileSearch, HelpCircle, Info, ListChecks, Loader2, Minus, Plus, UserPlus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/AuthProvider";
import type { AITopMatchesCandidate, AITopMatchesCriterionResult, AITopMatchesItem, AITopMatchesRun, AITopMatchesVerdict } from "@/hooks/jobs/useAITopMatches";
import { AITopMatchesBrief } from "./AITopMatchesBrief";

interface AITopMatchesResultsProps {
	run: AITopMatchesRun;
	candidatesById: Record<string, AITopMatchesCandidate>;
	existingCandidateIds: string[];
	adding: boolean;
	onAdd: (candidateIds: string[]) => Promise<number>;
}

const CRITERION_STYLES: Record<AITopMatchesCriterionResult["status"], string> = {
	met: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300",
	partial: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300",
	missing: "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300",
	unknown: "border-border bg-muted/40 text-muted-foreground",
};

const CRITERION_ICONS = { met: Check, partial: Minus, missing: X, unknown: HelpCircle };

// Same status colours as the candidate lists (Advanced Search results)
const STATUS_STYLES: Record<string, string> = {
	looking: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-400",
	open: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-400",
	new: "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950/30 dark:text-purple-400",
	unavailable: "border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-800 dark:bg-gray-950/30 dark:text-gray-400",
	hired: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400",
};

// No % score here: the match score is calculated by the full match analysis once a candidate is added.
// The list shows the AI's confidence instead, as a 4-segment gauge.
const TRUST_LEVELS: Record<AITopMatchesVerdict, { segments: number; stroke: string; text: string }> = {
	strong: { segments: 4, stroke: "stroke-emerald-600 dark:stroke-emerald-400", text: "text-emerald-700 dark:text-emerald-400" },
	possible: { segments: 3, stroke: "stroke-emerald-500 dark:stroke-emerald-500", text: "text-emerald-700 dark:text-emerald-400" },
	weak: { segments: 2, stroke: "stroke-amber-500", text: "text-amber-700 dark:text-amber-400" },
	no: { segments: 1, stroke: "stroke-gray-400 dark:stroke-gray-500", text: "text-muted-foreground" },
};

// Weak and long-shot results are still listed (the top 25 is always filled) below a divider
const isWeak = (item: AITopMatchesItem) => item.verdict === "weak" || item.verdict === "no";

// Semicircle split into 4 arcs, left to right
const GAUGE_ARCS = [0, 1, 2, 3].map((index) => {
	const point = (deg: number) => {
		const rad = (deg * Math.PI) / 180;
		return `${(20 + 16 * Math.cos(rad)).toFixed(2)} ${(20 - 16 * Math.sin(rad)).toFixed(2)}`;
	};
	return `M ${point(180 - index * 45 - 3)} A 16 16 0 0 1 ${point(180 - (index + 1) * 45 + 3)}`;
});

const formatDuration = (ms?: number) => {
	if (!ms)
		return "—";
	const seconds = Math.round(ms / 1000);
	return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

function CriterionChip({ criterion }: { criterion: AITopMatchesCriterionResult }) {
	const { t } = useTranslation();
	const Icon = CRITERION_ICONS[criterion.status];
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className={cn("inline-flex max-w-[240px] cursor-default items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px]", CRITERION_STYLES[criterion.status])}>
					<Icon className="h-3 w-3 shrink-0" />
					<span className="truncate">{criterion.label}</span>
				</span>
			</TooltipTrigger>
			<TooltipContent className="max-w-sm text-xs">
				<div className="font-medium">{t(`aiTopMatches.mustHave.${criterion.status}`)}</div>
				{criterion.evidence && <div className="mt-1 text-muted-foreground">{criterion.evidence}</div>}
			</TooltipContent>
		</Tooltip>
	);
}

function Stat({ icon: Icon, children, tooltip }: { icon: typeof Clock; children: React.ReactNode; tooltip?: string }) {
	const content = (
		<span className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-2 py-1 text-xs text-muted-foreground">
			<Icon className="h-3.5 w-3.5" />
			{children}
		</span>
	);
	if (!tooltip)
		return content;
	return (
		<Tooltip>
			<TooltipTrigger asChild>{content}</TooltipTrigger>
			<TooltipContent className="text-xs">{tooltip}</TooltipContent>
		</Tooltip>
	);
}

function TrustGauge({ verdict }: { verdict: AITopMatchesVerdict }) {
	const { t } = useTranslation();
	const level = TRUST_LEVELS[verdict] ?? TRUST_LEVELS.no;
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className="flex cursor-default flex-col items-center">
					<svg viewBox="0 0 40 22" className="h-[22px] w-10" aria-hidden="true">
						{GAUGE_ARCS.map((d, index) => (
							<path
								key={d}
								d={d}
								fill="none"
								strokeWidth={4}
								className={index < level.segments ? level.stroke : "stroke-gray-200 dark:stroke-gray-700"}
							/>
						))}
					</svg>
					<span className={cn("mt-0.5 whitespace-nowrap text-[10px] font-semibold leading-none", level.text)}>{t(`aiTopMatches.trust.${verdict}`)}</span>
				</div>
			</TooltipTrigger>
			<TooltipContent side="right" className="max-w-xs text-xs">
				<div className="font-medium">{t(`aiTopMatches.trust.${verdict}Tooltip`)}</div>
				<div className="mt-1 text-muted-foreground">{t("aiTopMatches.trust.note")}</div>
			</TooltipContent>
		</Tooltip>
	);
}

interface ResultRowProps {
	item: AITopMatchesItem;
	candidate?: AITopMatchesCandidate;
	inPipeline: boolean;
	selected: boolean;
	onToggle: () => void;
}

function ResultRow({ item, candidate, inPipeline, selected, onToggle }: ResultRowProps) {
	const { t } = useTranslation();
	const subtitle = [candidate?.function, candidate?.location].filter(Boolean).join(" · ");
	const weak = isWeak(item);

	return (
		<div
			className={cn(
				"group rounded-sm border border-border bg-card p-3 transition-colors hover:border-purple-200 dark:hover:border-purple-900",
				selected && "border-purple-400 bg-purple-50/50 hover:border-purple-400 dark:border-purple-700 dark:bg-purple-950/20",
				inPipeline && "bg-muted/30 opacity-60",
				weak && !selected && !inPipeline && "opacity-75 hover:opacity-100",
			)}
		>
			<div className="flex items-start gap-3">
				<Checkbox checked={selected} disabled={inPipeline} onCheckedChange={onToggle} className="mt-2" />
				<div className="flex w-14 shrink-0 flex-col items-center gap-1.5">
					<TrustGauge verdict={item.verdict} />
					<span className="text-[11px] font-medium text-muted-foreground">#{item.rank}</span>
				</div>
				<div className="min-w-0 flex-1 space-y-2">
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
						<Link to={`/candidates/${item.candidate_id}`} target="_blank" className="inline-flex items-center gap-1 text-sm font-semibold text-foreground hover:text-purple-700 hover:underline dark:hover:text-purple-300">
							{candidate?.name ?? t("aiTopMatches.results.unknownCandidate")}
							<ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
						</Link>
						{candidate?.status && (
							<Badge variant="outline" className={cn("rounded-sm px-1.5 py-0 text-[11px] font-normal", STATUS_STYLES[candidate.status])}>
								{t(`candidates.status.${candidate.status}`, candidate.status)}
							</Badge>
						)}
						{item.source === "scout" && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Badge variant="outline" className="cursor-default gap-1 rounded-sm border-purple-200 bg-purple-50 px-1.5 py-0 text-[11px] font-normal text-purple-700 dark:border-purple-800 dark:bg-purple-950/30 dark:text-purple-300">
										<Bot className="h-3 w-3" />
										{t("aiTopMatches.results.foundByScout")}
									</Badge>
								</TooltipTrigger>
								<TooltipContent className="max-w-sm text-xs">{item.scout_reason ?? t("aiTopMatches.results.foundByScoutTooltip")}</TooltipContent>
							</Tooltip>
						)}
						{item.duplicate_of_rank && (
							<Badge variant="outline" className="rounded-sm border-amber-200 bg-amber-50 px-1.5 py-0 text-[11px] font-normal text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
								{t("aiTopMatches.results.possibleDuplicate", { rank: item.duplicate_of_rank })}
							</Badge>
						)}
						{inPipeline && <Badge variant="secondary" className="rounded-sm px-1.5 py-0 text-[11px] font-normal">{t("aiTopMatches.results.alreadyInPipeline")}</Badge>}
					</div>
					{subtitle && <p className="-mt-1 text-xs text-muted-foreground">{subtitle}</p>}

					{item.summary && <p className="text-sm leading-relaxed text-foreground/90">{item.summary}</p>}

					{item.must_haves.length > 0 && (
						<div className="flex flex-wrap gap-1">
							{item.must_haves.map((criterion) => <CriterionChip key={criterion.id} criterion={criterion} />)}
						</div>
					)}

					{(item.strengths.length > 0 || item.gaps.length > 0) && (
						<div className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
							<ul className="space-y-0.5">
								{item.strengths.map((strength) => (
									<li key={strength} className="flex gap-1.5 text-foreground/80">
										<Plus className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />
										<span>{strength}</span>
									</li>
								))}
							</ul>
							<ul className="space-y-0.5">
								{item.gaps.map((gap) => (
									<li key={gap} className="flex gap-1.5 text-foreground/80">
										<Minus className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
										<span>{gap}</span>
									</li>
								))}
							</ul>
						</div>
					)}

					{(item.flags.length > 0 || item.availability_note) && (
						<div className="flex flex-wrap items-center gap-1.5 text-xs">
							{item.flags.map((flag) => (
								<span key={flag} className="inline-flex items-center gap-1 rounded-sm border border-red-200 bg-red-50 px-1.5 py-0.5 text-[11px] text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
									<AlertTriangle className="h-3 w-3" />
									{t(`aiTopMatches.flags.${flag}`, flag)}
								</span>
							))}
							{item.availability_note && <span className="text-muted-foreground">{item.availability_note}</span>}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export function AITopMatchesResults({ run, candidatesById, existingCandidateIds, adding, onAdd }: AITopMatchesResultsProps) {
	const { t } = useTranslation();
	const { isSuperAdmin } = useAuth();
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const items = run.results?.items ?? [];
	const coverage = run.results?.coverage ?? [];
	const pipeline = useMemo(() => new Set(existingCandidateIds), [existingCandidateIds]);
	const selectable = items.filter((item) => !pipeline.has(item.candidate_id)).map((item) => item.candidate_id);
	const allSelected = selectable.length > 0 && selectable.every((id) => selected.has(id));
	const firstWeakIndex = items.findIndex(isWeak);
	const stats = run.stats ?? {};

	const toggle = (id: string) => setSelected((prev) => {
		const next = new Set(prev);
		if (next.has(id))
			next.delete(id);
		else
			next.add(id);
		return next;
	});

	const handleAdd = async () => {
		const ids = [...selected].filter((id) => !pipeline.has(id));
		const added = await onAdd(ids);
		if (added > 0)
			setSelected(new Set());
	};

	return (
		<TooltipProvider delayDuration={200}>
			<div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
				<div className="flex flex-wrap items-center gap-2">
					<Stat icon={ListChecks}>{t("aiTopMatches.results.statScreened", { count: stats.screened ?? stats.pool_estimate ?? 0 })}</Stat>
					<Stat icon={FileSearch}>{t("aiTopMatches.results.statReviewed", { count: stats.deep_checked ?? 0 })}</Stat>
					<Stat icon={Clock}>{formatDuration(stats.duration_ms)}</Stat>
					{isSuperAdmin && (
						<Stat icon={Coins} tooltip={stats.model ?? undefined}>{t("aiTopMatches.results.statCost", { cost: (stats.cost_usd ?? 0).toFixed(3) })}</Stat>
					)}
					{stats.decider === "chat" && (
						<span className="text-xs text-muted-foreground">{t("aiTopMatches.results.fallbackNotice")}</span>
					)}
				</div>

				{coverage.length > 0 && items.length > 0 && (
					<div className="rounded-sm border border-border bg-muted/30 px-3 py-2.5">
						<p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("aiTopMatches.results.coverageTitle", { count: items.length })}</p>
						<div className="flex flex-wrap gap-x-4 gap-y-1.5">
							{coverage.map((c) => (
								<div key={c.id} className="flex min-w-[180px] items-center gap-2 text-xs">
									<span className="max-w-[200px] truncate text-foreground">{c.label}</span>
									<span className="h-1.5 w-16 overflow-hidden rounded-sm bg-muted">
										<span className="block h-full rounded-sm bg-emerald-500" style={{ width: `${(c.met / items.length) * 100}%` }} />
									</span>
									<span className="tabular-nums text-muted-foreground">{c.met}/{items.length}</span>
								</div>
							))}
						</div>
					</div>
				)}

				{run.brief && <AITopMatchesBrief brief={run.brief} stats={stats} />}

				<div className="space-y-2">
					{items.length > 0 && (
						<p className="flex items-center gap-1.5 text-xs text-muted-foreground">
							<Info className="h-3.5 w-3.5 shrink-0" />
							{t("aiTopMatches.trust.note")}
						</p>
					)}
					{items.length === 0 && (
						<div className="rounded-sm border border-dashed border-border p-8 text-center text-sm text-muted-foreground">{t("aiTopMatches.results.empty")}</div>
					)}
					{items.map((item, index) => (
						<div key={item.candidate_id} className="space-y-2">
							{index === firstWeakIndex && index > 0 && (
								<div className="flex items-center gap-3 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
									<span className="h-px flex-1 bg-border" />
									{t("aiTopMatches.results.weakDivider")}
									<span className="h-px flex-1 bg-border" />
								</div>
							)}
							<ResultRow
								item={item}
								candidate={candidatesById[item.candidate_id]}
								inPipeline={pipeline.has(item.candidate_id)}
								selected={selected.has(item.candidate_id)}
								onToggle={() => toggle(item.candidate_id)}
							/>
						</div>
					))}
				</div>
			</div>

			<div className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-background px-5 py-3">
				<div className="flex items-center gap-3">
					<Checkbox
						id="ai-top-matches-select-all"
						checked={allSelected}
						disabled={selectable.length === 0}
						onCheckedChange={() => setSelected(allSelected ? new Set() : new Set(selectable))}
					/>
					<label htmlFor="ai-top-matches-select-all" className="cursor-pointer text-sm">{t("aiTopMatches.results.selectAll")}</label>
				</div>
				<Button size="sm" onClick={handleAdd} disabled={adding || selected.size === 0} className="bg-purple-600 hover:bg-purple-700">
					{adding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <UserPlus className="mr-1.5 h-4 w-4" />}
					{adding ? t("aiTopMatches.results.adding") : t("aiTopMatches.results.addSelected", { count: selected.size })}
				</Button>
			</div>
		</TooltipProvider>
	);
}
